export enum OperationStatus {
  ConfirmationRequired = 'confirmationRequired',
  Entrapment = 'entrapment',
  FollowUpRequired = 'followUpRequired',
  PhysicalDamage = 'physicalDamage',
  PowerOutage = 'powerOutage',
  RecoveryCompleted = 'recoveryCompleted',
  SiteInaccessible = 'siteInaccessible',
  Suspended = 'suspended',
  Unknown = 'unknown',
  TempRecovery = 'tempRecovery',
  Incomplete = 'incomplete',
  None = 'none',
}

export const defaultSatusOrder = [
  OperationStatus.None,
  OperationStatus.Entrapment,
  OperationStatus.FollowUpRequired,
  OperationStatus.Suspended,
  OperationStatus.TempRecovery,
  OperationStatus.ConfirmationRequired,
  OperationStatus.PhysicalDamage,
  OperationStatus.SiteInaccessible,
  OperationStatus.RecoveryCompleted,
  OperationStatus.Unknown,
  OperationStatus.Incomplete,
  OperationStatus.PowerOutage,
];

export function sortStatusOptions<T, K extends keyof T>(
  array: readonly T[],
  key: K,
  order: readonly NonNullable<T[K]>[]
): T[] {
  const map = new Map<NonNullable<T[K]>, number>(order.map((v, i) => [v, i]));

  return [...array].sort((a, b) => {
    const av = a[key] as NonNullable<T[K]>;
    const bv = b[key] as NonNullable<T[K]>;

    const ai = map.get(av) ?? Number.POSITIVE_INFINITY;
    const bi = map.get(bv) ?? Number.POSITIVE_INFINITY;
    return ai - bi;
  });
}

export function getDetailStatusTranslationKey(status: string): string {
  return `wards.common.statusDetail.${status}`;
}

export function getOperationStatusTranslationKey(status: OperationStatus): string {
  return `wards.common.statusCodes.${status}`;
}

export function getStatusFromStatusCode(statusCode: string): OperationStatus {
  if (['65'].some((item) => item === statusCode)) {
    return OperationStatus.Entrapment;
  }
  if (['44'].some((item) => item === statusCode)) {
    return OperationStatus.FollowUpRequired;
  }
  if (
    ['41', '80', '51', '47', '52', '53', '46', '48', '49', '45', '29'].some(
      (item) => item === statusCode
    )
  ) {
    return OperationStatus.Suspended;
  }
  if (['43'].some((item) => item === statusCode)) {
    return OperationStatus.ConfirmationRequired;
  }
  if (['28', '54'].some((item) => item === statusCode)) {
    return OperationStatus.TempRecovery;
  }
  if (['42', '21', '00'].some((item) => item === statusCode)) {
    return OperationStatus.Unknown;
  }
  if (['55', '71', '72', '73', '74', '75'].some((item) => item === statusCode)) {
    return OperationStatus.PhysicalDamage;
  }
  if (['02', '23', '24'].some((item) => item === statusCode)) {
    return OperationStatus.SiteInaccessible;
  }
  if (['01', '26', '27', '22', '56', '57'].some((item) => item === statusCode)) {
    return OperationStatus.RecoveryCompleted;
  }
  if (['31'].some((item) => item === statusCode)) {
    return OperationStatus.PowerOutage;
  }
  return OperationStatus.Unknown;
}

export function operationStatusCodes(status: OperationStatus): string[] {
  switch (status) {
    case OperationStatus.Entrapment:
      return ['65'];
    case OperationStatus.FollowUpRequired:
      return ['44'];
    case OperationStatus.Suspended:
      return ['41', '80', '51', '47', '52', '53', '46', '48', '49', '45', '29'];
    case OperationStatus.ConfirmationRequired:
      return ['43'];
    case OperationStatus.Unknown:
      return ['42', '21', '00'];
    case OperationStatus.TempRecovery:
      return ['28', '54'];
    case OperationStatus.PhysicalDamage:
      return ['55', '71', '72', '73', '74', '75'];
    case OperationStatus.SiteInaccessible:
      return ['02', '23', '24'];
    case OperationStatus.RecoveryCompleted:
      return ['01', '26', '27', '22', '56', '57'];
    case OperationStatus.Incomplete:
      return [
        '00',
        '02',
        '21',
        '23',
        '24',
        '28',
        '29',
        '41',
        '80',
        '42',
        '43',
        '44',
        '45',
        '46',
        '47',
        '48',
        '49',
        '51',
        '52',
        '53',
        '54',
        '65',
      ];
    case OperationStatus.PowerOutage:
      return ['31'];
    case OperationStatus.None:
      return [
        '00',
        '01',
        '02',
        '21',
        '22',
        '23',
        '24',
        '26',
        '27',
        '28',
        '29',
        '31',
        '41',
        '80',
        '42',
        '43',
        '44',
        '45',
        '46',
        '47',
        '48',
        '49',
        '51',
        '52',
        '53',
        '54',
        '55',
        '56',
        '57',
        '65',
        '71',
        '72',
        '73',
        '74',
        '75',
      ];
  }
}

export function getMapPinPropsFromNumbeCode(code: string) {
  return getMapPinProps(getStatusFromStatusCode(code));
}

export function getMapPinProps(operationStatus: OperationStatus) {
  switch (operationStatus) {
    case OperationStatus.Entrapment:
      return { fillColor: 'text-red-500', lineColor: 'text-black', dotColor: 'bg-white' };
    case OperationStatus.FollowUpRequired:
      return { fillColor: 'text-orange-400', lineColor: 'text-black', dotColor: 'bg-white' };
    case OperationStatus.Suspended:
      return { fillColor: 'text-yellow-200', lineColor: 'text-black', dotColor: 'bg-white' };
    case OperationStatus.TempRecovery:
      return { fillColor: 'text-amber-800', lineColor: 'text-black', dotColor: 'bg-white' };
    case OperationStatus.ConfirmationRequired:
      return { fillColor: 'text-blue-500', lineColor: 'text-black', dotColor: 'bg-white' };
    case OperationStatus.PhysicalDamage:
      return { fillColor: 'text-pink-300', lineColor: 'text-black', dotColor: 'bg-white' };
    case OperationStatus.SiteInaccessible:
      return { fillColor: 'text-emerald-200', lineColor: 'text-black', dotColor: 'bg-white' };
    case OperationStatus.RecoveryCompleted:
      return { fillColor: 'text-sky-200', lineColor: 'text-black', dotColor: 'bg-white' };
    case OperationStatus.PowerOutage:
      return { fillColor: 'text-green-900', lineColor: 'text-black', dotColor: 'bg-white' };
    case OperationStatus.Unknown:
      return { fillColor: 'text-white', lineColor: 'text-black', dotColor: 'bg-white' };
  }
}

/**
 * statusId の優先順位に基づいて AdvancedMarker の zIndex 値を返す。
 * リスト先頭ほど優先度が高く（手前に表示）、リスト外・null は最背面となる。
 */
const WORKPLACE_MARKER_Z_INDEX_ORDER: readonly string[] = [
  '65',
  '44',
  '80',
  '41',
  '51',
  '47',
  '52',
  '53',
  '46',
  '49',
  '45',
  '29',
  '28',
  '54',
  '43',
  '42',
  '21',
  '55',
  '71',
  '72',
  '73',
  '74',
  '75',
  '31',
  '23',
  '24',
  '02',
  '26',
  '27',
  '22',
  '56',
  '01',
  '57',
  '00',
];

export function getWorkplaceMarkerZIndex(statusId: string | null | undefined): number {
  if (statusId == null) return 0;
  const index = WORKPLACE_MARKER_Z_INDEX_ORDER.indexOf(statusId);
  if (index === -1) return 0;
  return WORKPLACE_MARKER_Z_INDEX_ORDER.length - index;
}
